/* eslint-disable max-lines */
/**
 * Parent-Athlete Relationship Utilities
 *
 * REQUIREMENTS SUMMARY:
 * - Keep parent-athlete relationship reads as the source of truth across web and mobile.
 * - Allow callers to pass an authenticated/server Supabase client when running inside API routes.
 * - (U2 Family / Guardian Access) Adds an ADDITIVE, organization-scoped
 *   compatibility helper `assertRelationshipGrantsAccessInOrg` that legacy
 *   consumers can call to verify a relationship grant within an org + (optionally)
 *   the new family-account context. While ENABLE_FAMILY_GUARDIAN_ACCESS is OFF it
 *   collapses to a plain "does an active relationship exist for this parent +
 *   athlete in this org" check, so behavior is unchanged. No existing export is
 *   modified. The richer family-permission decision lives in
 *   FamilyAccountService (the canonical boundary); this helper is only the thin
 *   bridge so callers do not branch on raw flags.
 * - (Support ticket #93) Active parent/guardian lists must not show a revoked
 *   Family Access guardian as if they are still connected. Historical
 *   parent_athlete_relationships rows stay in place, but display helpers filter
 *   out family guardians whose current family row is not active.
 *
 * SINGLE SOURCE OF TRUTH for fetching parent-athlete relationships
 * 
 * ✅ UPDATED 2025-11-13: parent_athlete_relationships.parent_id NOW STORES auth_users.id DIRECTLY
 * The schema was migrated to use auth_user_id as the foreign key.
 * NO conversion needed - parent_id = auth_user_id
 */

import { supabase } from '@/utils/supabase/client';
import { createLogger } from '@/utils/logger/devLogger';
import { FEATURE_FLAGS } from '@/utils/config/featureFlags';
import { fetchInChunks } from '@/utils/supabase/fetchInChunks';
import { fetchAllPages } from '@/utils/supabase/fetchAllPages';
import { isSelfManagedAthlete } from './guardianlessAthlete';
import { fromParentLinks, isServiceRoleClient } from './parentLinkSource';
import { dropRevokedLinks, readStaffRevokedLinkKeys } from './staffRevokedLinks';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';

const logger = createLogger('ParentAthleteRelationships');

export interface ParentAthleteRelationship {
  id: string;
  parent_id: string;
  athlete_id: string;
  relationship: string;
  is_primary: boolean;
  has_billing_access: boolean;
  has_medical_access: boolean;
  created_at?: string;
  updated_at?: string;
  athlete?: {
    id: string;
    first_name: string;
    last_name: string;
    date_of_birth: string;
    gender?: string;
    profile_image_url?: string;
    status?: string;
  };
}

/**
 * Validates if a user (auth_user_id) is authorized to access/manage an athlete.
 * A user is authorized if:
 * 1. They have a parent-athlete relationship with the athlete (includes the
 *    "self via self-relationship row" case introduced by plan 2026-05-23-002
 *    U3's backfill — a parent_athlete_relationships row where
 *    parent_id === athlete.auth_user_id)
 * 2. They are the athlete themselves (legacy Independent Athlete shape:
 *    athletes.auth_user_id direct match, no self-relationship row yet)
 *
 * Return flags:
 * - `authorized`: true when the user can access this athlete
 * - `isParent`: true when access path is via a parent_athlete_relationships row
 *   (true for both "parent of someone else" AND "self via self-relationship")
 * - `isSelf`: true when access path is via the legacy direct
 *   athletes.auth_user_id match (no self-relationship row exists yet)
 * - `isSelfViaRelationship`: true when access path is the parent-relationship
 *   branch AND the relationship row is a self-row (parent_id === athlete.auth_user_id).
 *   Distinguishes "the athlete themselves via the new dual-shape" from
 *   "parent of another athlete" so callers can branch correctly. After U3's
 *   backfill, the 13 migrated adult-athlete accounts will return
 *   { isParent: true, isSelfViaRelationship: true } via this branch instead
 *   of the legacy { isSelf: true } branch.
 *
 * @param client - Supabase client (should have appropriate permissions)
 * @param authUserId - The auth user ID to check
 * @param athleteId - The athlete ID to check
 * @returns Object indicating authorization status and role
 */
export async function validateUserAthleteAccess(
  client: SupabaseClient<Database>,
  authUserId: string,
  athleteId: string
): Promise<{
  authorized: boolean;
  isParent: boolean;
  isSelf: boolean;
  isSelfViaRelationship: boolean;
  relationshipId?: string;
  /**
   * Set (true) ONLY when the check itself FAILED (query error / throw) rather
   * than a real "no access" result. `authorized` stays false (fail closed);
   * callers that want honest UX answer 500 + retry copy instead of denial
   * copy (U3, R5). Absent on every real allow/deny.
   */
  checkFailed?: true;
}> {
  const checkFailedResult = {
    authorized: false,
    isParent: false,
    isSelf: false,
    isSelfViaRelationship: false,
    checkFailed: true as const,
  };
  try {
    // 1. Check parent-athlete relationship.
    // After plan 2026-05-23-002 U2's R8 lift, a self-row may exist where
    // parent_id === athlete.auth_user_id. Pull athlete.auth_user_id alongside
    // so we can tag the row as self-via-relationship vs parent-of-another.
    const { data: relationship, error: relationshipError } = await fromParentLinks(client)
      .select('id, athletes!inner(auth_user_id)')
      .eq('parent_id', authUserId)
      .eq('athlete_id', athleteId)
      .maybeSingle() as { data: { id: string; athletes?: { auth_user_id?: string | null } | Array<{ auth_user_id?: string | null }> } | null; error: any };

    if (relationshipError) {
      logger.error('validateUserAthleteAccess: relationship read failed', { authUserId, athleteId, error: relationshipError });
      return checkFailedResult;
    }

    if (relationship) {
      const athleteJoin = Array.isArray(relationship.athletes)
        ? relationship.athletes[0]
        : relationship.athletes;
      const athleteAuthUserId = athleteJoin?.auth_user_id ?? null;
      const isSelfViaRelationship = !!athleteAuthUserId && athleteAuthUserId === authUserId;
      return {
        authorized: true,
        isParent: true,
        isSelf: false,
        isSelfViaRelationship,
        relationshipId: relationship.id
      };
    }

    // 2. Check if user is the athlete themselves (their own athlete login).
    const { data: selfRecord, error: selfError } = await client
      .from('athletes')
      .select('id')
      .eq('id', athleteId)
      .eq('auth_user_id', authUserId)
      .maybeSingle();

    if (selfError) {
      logger.error('validateUserAthleteAccess: self-athlete read failed', { authUserId, athleteId, error: selfError });
      return checkFailedResult;
    }

    if (selfRecord) {
      return {
        authorized: true,
        isParent: false,
        isSelf: true,
        isSelfViaRelationship: false
      };
    }

    return { authorized: false, isParent: false, isSelf: false, isSelfViaRelationship: false };
  } catch (error) {
    logger.error('Error validating user-athlete access:', { authUserId, athleteId, error });
    return checkFailedResult;
  }
}

/**
 * Fetch parent-athlete relationships by parent ID directly
 * 
 * ✅ USE THIS FUNCTION when you already have the parent auth_user_id
 * 
 * ✅ UPDATED 2025-11-13: parentId now refers to auth_user_id (not parents table ID)
 * The schema was migrated so parent_athlete_relationships.parent_id = auth_users.id
 * 
 * This is now functionally identical to getParentAthleteRelationshipsByAuthUserId()
 * but kept for backward compatibility with code that uses "parentId" terminology.
 * 
 * @param parentId - The auth user ID (same as auth_users.id)
 * @param includeAthleteDetails - Whether to include full athlete details (default: true)
 * @param client - Optional Supabase client to use
 * @returns Array of parent-athlete relationships with athlete details
 * 
 * @example
 * // When you already have auth_user_id
 * const relationships = await getParentAthleteRelationshipsByParentId(authUserId);
 */
export async function getParentAthleteRelationshipsByParentId(
  parentId: string,
  includeAthleteDetails: boolean = true,
  client?: SupabaseClient<Database>
): Promise<ParentAthleteRelationship[]> {
  try {
    const dbClient: SupabaseClient<Database> = client || supabase;
    
    logger.info('Fetching parent-athlete relationships by parent ID:', { parentId });

    const selectQuery = includeAthleteDetails 
      ? `
          *,
          athletes!inner (
            id,
            first_name,
            last_name,
            date_of_birth,
            profile_image_url
          )
        `
      : '*';

    const { data: relationships, error: relationshipsError } = await fromParentLinks(dbClient)
      .select(selectQuery)
      .eq('parent_id', parentId) as { data: any[] | null; error: any };

    if (relationshipsError) {
      logger.error('Error fetching relationships by parent ID:', relationshipsError);
      return [];
    }

    logger.info(`Found ${relationships?.length || 0} parent-athlete relationships`, {
      parentId
    });

    // Transform Supabase response: 'athletes' (plural) → 'athlete' (singular) to match interface
    const transformedRelationships = relationships?.map(rel => ({
      ...rel,
      athlete: rel.athletes // Rename athletes → athlete
    })) || [];

    return transformedRelationships;
  } catch (error) {
    logger.error('Unexpected error fetching relationships by parent ID:', error);
    return [];
  }
}

/**
 * Fetch parent-athlete relationships by auth user ID
 * 
 * ✅ USE THIS FUNCTION when you have the authenticated user's ID (auth.uid() or session.user.id)
 * 
 * ✅ UPDATED 2025-11-13: Direct query - parent_id now stores auth_user_id directly
 * No conversion needed - just query parent_athlete_relationships.parent_id = authUserId
 * 
 * @param authUserId - The auth user ID (from auth.users or session.user.id)
 * @param includeAthleteDetails - Whether to include full athlete details (default: true)
 * @param client - Optional Supabase client to use (for server-side with admin privileges)
 * @returns Array of parent-athlete relationships with athlete details
 * 
 * @example
 * // In a component or page (parent viewing their own)
 * const { data: { user } } = await supabase.auth.getUser();
 * const relationships = await getParentAthleteRelationshipsByAuthUserId(user.id);
 * 
 * @example
 * // Server-side with admin client (bypasses RLS)
 * import { supabaseAdmin } from '@/utils/supabase/server';
 * const relationships = await getParentAthleteRelationshipsByAuthUserId(parentAuthUserId, true, supabaseAdmin);
 */
export async function getParentAthleteRelationshipsByAuthUserId(
  authUserId: string,
  includeAthleteDetails: boolean = true,
  client?: SupabaseClient<Database>
): Promise<ParentAthleteRelationship[]> {
  try {
    logger.info('Fetching parent-athlete relationships by auth user ID:', { authUserId });

    // Use provided client or default to supabase
    const dbClient: SupabaseClient<Database> = client || supabase;

    // ✅ SIMPLIFIED: parent_id now stores auth_user_id directly, no conversion needed
    // auth_user_id is selected so callers can detect the "self" case via
    // `rel.parent_id === rel.athlete.auth_user_id` (robust against the legacy
    // bug where the self-row was historically stored with relationship='parent').
    const selectQuery = includeAthleteDetails
      ? `
          *,
          athletes!inner (
            id,
            first_name,
            last_name,
            date_of_birth,
            gender,
            profile_image_url,
            status,
            auth_user_id
          )
        `
      : '*';

    const { data: relationships, error: relationshipsError } = await fromParentLinks(dbClient)
      .select(selectQuery)
      .eq('parent_id', authUserId) as { data: any[] | null; error: any };

    if (relationshipsError) {
      logger.error('Error fetching relationships:', relationshipsError);
      throw new Error(`Failed to fetch relationships: ${relationshipsError.message}`);
    }

    logger.info(`Found ${relationships?.length || 0} parent-athlete relationships`, {
      authUserId
    });

    // Transform Supabase response: 'athletes' (plural) → 'athlete' (singular) to match interface
    const transformedRelationships = relationships?.map(rel => ({
      ...rel,
      athlete: rel.athletes // Rename athletes → athlete
    })) || [];

    return transformedRelationships;
  } catch (error) {
    logger.error('Error in getParentAthleteRelationshipsByAuthUserId:', error);
    throw error;
  }
}

/**
 * The athletes a login sees on the parent/athlete dashboard: every
 * parent_athlete_relationships row it holds, plus its own athlete record
 * (athletes.auth_user_id = login) when no row links it. An invited athlete
 * login (teen or 18+) has no row of its own, only its parent's, so without
 * the second read it sees an empty "Parent Portal".
 *
 * The added entry is display-only: `is_own_login: true`, no id, no billing or
 * medical access. It is not a self row and does not make the athlete
 * self-managed (see isSelfManagedAthlete).
 */
export type DashboardAthleteRelationship = ParentAthleteRelationship & {
  is_own_login?: boolean;
};

export async function getDashboardAthleteRelationshipsByAuthUserId(
  authUserId: string,
  client?: SupabaseClient<Database>
): Promise<DashboardAthleteRelationship[]> {
  const dbClient: SupabaseClient<Database> = client || supabase;
  const relationships: DashboardAthleteRelationship[] =
    await getParentAthleteRelationshipsByAuthUserId(authUserId, true, dbClient);

  const { data: ownAthletes, error } = await dbClient
    .from('athletes')
    .select('id, first_name, last_name, date_of_birth, gender, profile_image_url, status, auth_user_id')
    .eq('auth_user_id', authUserId);

  if (error) {
    throw new Error(`Failed to fetch own athlete record: ${error.message}`);
  }

  const linkedAthleteIds = new Set(relationships.map((rel) => rel.athlete_id));
  for (const athlete of ownAthletes || []) {
    if (linkedAthleteIds.has(athlete.id)) continue;
    relationships.push({
      id: '',
      parent_id: authUserId,
      athlete_id: athlete.id,
      relationship: 'own_login',
      is_primary: false,
      has_billing_access: false,
      has_medical_access: false,
      athlete: athlete as ParentAthleteRelationship['athlete'],
      is_own_login: true,
    });
  }

  return relationships;
}

/**
 * Get athlete IDs for a parent by auth user ID
 *
 * Convenience function to just get the list of athlete IDs
 * 
 * @param authUserId - The auth user ID
 * @param client - Optional Supabase client to use
 * @returns Array of athlete IDs
 * 
 * @example
 * const athleteIds = await getParentAthleteIds(user.id);
 * // Use athleteIds for further queries
 */
export async function getParentAthleteIds(
  authUserId: string,
  client?: SupabaseClient<Database>
): Promise<string[]> {
  try {
    const relationships = await getParentAthleteRelationshipsByAuthUserId(authUserId, false, client);
    return relationships.map(rel => rel.athlete_id);
  } catch (error) {
    logger.error('Error getting parent athlete IDs:', error);
    return [];
  }
}

/**
 * Get primary parent info for multiple athletes (batched).
 * Returns a Map of athlete_id -> { first_name, last_name, email } for the primary parent.
 * Athletes without a parent or with no primary parent are omitted from the map.
 *
 * IMPORTANT (#1418): the return distinguishes "read failed" from "no parents".
 *   - `Map` (possibly empty) = the read SUCCEEDED; athletes absent from the map
 *     genuinely have no primary parent.
 *   - `null` = the read FAILED (query error or thrown exception). Callers must
 *     NOT treat a failed read as "no parents" — doing so paints the amber
 *     "No family" badge on every row on a transient error. On null, keep the
 *     badge hidden and try again on the next refresh.
 *
 * @param athleteIds - Array of athlete IDs
 * @param client - Optional Supabase client to use
 * @returns Map of athlete_id to parent { first_name, last_name, email }, or null on read failure
 *
 * @example
 * const parentMap = await getPrimaryParentsForAthletes(athleteIds);
 * if (parentMap) {
 *   const parent = parentMap.get(athleteId);
 * }
 */
export async function getPrimaryParentsForAthletes(
  athleteIds: string[],
  client?: SupabaseClient<Database>
): Promise<Map<string, { first_name?: string; last_name?: string; email?: string }> | null> {
  const result = new Map<string, { first_name?: string; last_name?: string; email?: string }>();
  if (!athleteIds.length) return result;

  try {
    const dbClient: SupabaseClient<Database> = client || supabase;
    // Chunked: the staff Athletes list calls this with the entire roster, and a
    // whole-roster id list overflows the request URL (#2669).
    const { rows: relationships, error } = await fetchInChunks<any>(athleteIds, (chunk) =>
      fromParentLinks(dbClient)
        .select(`
          athlete_id,
          parent_id,
          is_primary,
          auth_users!parent_athlete_relationships_parent_id_fkey (
            first_name,
            last_name,
            email
          )
        `)
        .in('athlete_id', chunk) as PromiseLike<{ data: any[] | null; error: any }>);

    if (error) {
      // Return null (not an empty Map) so callers can tell a real read failure
      // apart from a genuine "no parents found" result (#1418).
      logger.error('Error fetching primary parents for athletes:', error);
      return null;
    }

    // #5966: on a staff login the table still holds links a family removed
    // (staff are exempt from that RLS rule). Never name a removed adult as the
    // athlete's parent; a failed check is a failed read.
    const revoked = isServiceRoleClient(dbClient) ? null : await readStaffRevokedLinkKeys(dbClient, athleteIds);
    if (!isServiceRoleClient(dbClient) && !revoked) {
      logger.error('Error checking removed Family Access links for primary parents');
      return null;
    }
    const liveRelationships = dropRevokedLinks(
      relationships as Array<{ parent_id: string; athlete_id: string; is_primary?: boolean | null }>,
      revoked?.all ?? new Set<string>()
    );

    // Sort so primary parent is processed first per athlete
    const sorted = [...liveRelationships].sort((a, b) =>
      (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)
    );
    sorted.forEach((rel: any) => {
      const authUser = rel.auth_users;
      if (!authUser) return;
      const athleteId = rel.athlete_id;
      if (!result.has(athleteId)) {
        result.set(athleteId, {
          first_name: authUser.first_name,
          last_name: authUser.last_name,
          email: authUser.email
        });
      }
    });
    return result;
  } catch (err) {
    // Same as above: a thrown error is a read failure, not "no parents" (#1418).
    logger.error('Error in getPrimaryParentsForAthletes:', err);
    return null;
  }
}

/**
 * Batch form of the one "self-managed" rule (#5413 KTD1, isSelfManagedAthlete
 * in ./guardianlessAthlete). Returns `Map<athleteId, boolean>` with an entry for
 * every id it could read, or null on a read failure (never a partial answer).
 * Chunked like getPrimaryParentsForAthletes (#2669).
 */
export async function getSelfManagedAthleteIds(
  athleteIds: string[],
  client?: SupabaseClient<Database>
): Promise<Map<string, boolean> | null> {
  const result = new Map<string, boolean>();
  if (!athleteIds.length) return result;
  const dbClient: SupabaseClient<Database> = client || supabase;

  try {
    const { rows: athletes, error: athleteError } = await fetchInChunks<{
      id: string;
      auth_user_id: string | null;
    }>(athleteIds, (chunk) =>
      dbClient
        .from('athletes')
        .select('id, auth_user_id')
        .in('id', chunk) as PromiseLike<{ data: any[] | null; error: any }>);
    if (athleteError) {
      logger.error('getSelfManagedAthleteIds: athlete read failed', athleteError);
      return null;
    }

    const loginByAthlete = new Map<string, string>();
    for (const athlete of athletes) {
      if (athlete.auth_user_id) loginByAthlete.set(athlete.id, athlete.auth_user_id);
    }
    // Only an athlete's OWN login can make a self row, so read just those rows.
    const { rows: selfRows, error: relError } = await fetchInChunks<{
      athlete_id: string;
      parent_id: string;
    }>([...loginByAthlete.keys()], (chunk) =>
      fromParentLinks(dbClient)
        .select('athlete_id, parent_id')
        .in('athlete_id', chunk)
        .in('parent_id', chunk.map((id) => loginByAthlete.get(id)!)) as PromiseLike<{
        data: any[] | null;
        error: any;
      }>);
    if (relError) {
      logger.error('getSelfManagedAthleteIds: relationship read failed', relError);
      return null;
    }

    const parentIdsByAthlete = new Map<string, string[]>();
    for (const row of selfRows) {
      const list = parentIdsByAthlete.get(row.athlete_id) ?? [];
      list.push(row.parent_id);
      parentIdsByAthlete.set(row.athlete_id, list);
    }
    for (const athlete of athletes) {
      result.set(athlete.id, isSelfManagedAthlete(athlete, parentIdsByAthlete.get(athlete.id) ?? []));
    }
    return result;
  } catch (err) {
    logger.error('Error in getSelfManagedAthleteIds:', err);
    return null;
  }
}

/**
 * The caller's own self-managed athlete (#5413 one rule): an athlete whose
 * login is `authUserId` AND that has its own relationship row. Returns the id,
 * null when the login has none, or 'read_failed' — never a guess.
 */
export async function getOwnSelfManagedAthleteId(
  authUserId: string,
  client?: SupabaseClient<Database>
): Promise<string | null | 'read_failed'> {
  const dbClient: SupabaseClient<Database> = client || supabase;
  const { data, error } = await dbClient
    .from('athletes')
    .select('id')
    .eq('auth_user_id', authUserId);
  if (error) {
    logger.error('getOwnSelfManagedAthleteId: athlete read failed', error);
    return 'read_failed';
  }
  const ids = ((data ?? []) as Array<{ id: string }>).map((row) => row.id);
  if (ids.length === 0) return null;
  const selfManaged = await getSelfManagedAthleteIds(ids, dbClient);
  if (!selfManaged) return 'read_failed';
  return ids.filter((id) => selfManaged.get(id) === true).sort()[0] ?? null;
}

/**
 * Get parent record ID from auth user ID
 * 
 * NOTE: Some legacy tables (like travel_email_submissions) still store the 
 * internal parents.id rather than auth_user_id. Use this function when 
 * querying those tables.
 * 
 * @param authUserId - The auth user ID
 * @returns The parents.id (internal UUID)
 * 
 * @example
 * const parentId = await getParentIdFromAuthUserId(user.id);
 */
export async function getParentIdFromAuthUserId(
  authUserId: string,
  client?: SupabaseClient<Database>
): Promise<string | null> {
  try {
    const dbClient: SupabaseClient<Database> = client || supabase;
    const { data, error } = await dbClient
      .from('parents')
      .select('id')
      .eq('auth_user_id', authUserId)
      .maybeSingle();

    if (error) {
      logger.error('Error fetching parent ID:', error);
      return null;
    }

    return data?.id || null;
  } catch (error) {
    logger.error('Error in getParentIdFromAuthUserId:', error);
    return null;
  }
}

/**
 * Does this athlete already have a primary parent/guardian relationship row?
 *
 * Use this before creating a NEW relationship row to decide `isPrimary`
 * (#2781 Phase 3): the athlete's first linked adult is the primary contact;
 * any adult linked after that must not become primary as a side effect.
 * Two primaries per athlete is valid ONLY as a deliberate Family Access
 * outcome (each adult heads their own household) — never as a default.
 *
 * On a read error this returns false, so the caller behaves as if no primary
 * exists (the new row becomes primary — the pre-#2781 behavior). Erring the
 * other way could leave a brand-new athlete with no primary contact, which
 * downstream recipient resolvers treat as "no one to notify".
 */
export async function athleteHasPrimaryParent(
  athleteId: string,
  client?: SupabaseClient<Database>
): Promise<boolean> {
  const dbClient: SupabaseClient<Database> = client || supabase;
  try {
    const { data, error } = await fromParentLinks(dbClient)
      .select('id')
      .eq('athlete_id', athleteId)
      .eq('is_primary', true)
      .limit(1)
      .maybeSingle();

    if (error) {
      logger.error('athleteHasPrimaryParent read failed; assuming no primary:', { athleteId, error });
      return false;
    }
    return !!data;
  } catch (error) {
    logger.error('Error in athleteHasPrimaryParent; assuming no primary:', { athleteId, error });
    return false;
  }
}

/**
 * Create parent-athlete relationship using auth user ID
 *
 * ✅ USE THIS FUNCTION when creating relationships with authenticated parent's ID
 *
 * ✅ UPDATED 2025-11-13: Simplified - parent_id now stores auth_user_id directly
 * No conversion needed - just pass auth_user_id as parent_id
 *
 * ⚠️ UPDATED 2026-08-01 (#2781 Phase 3): `relationship` and `isPrimary` are now
 * required. `isPrimary` previously defaulted to true, which silently minted a
 * second primary whenever a second adult was linked to an existing athlete.
 * When linking to an athlete that may already have parents, decide with
 * `athleteHasPrimaryParent` first.
 *
 * @param authUserId - The parent's auth user ID (from auth.users or session.user.id)
 * @param athleteId - The athlete's ID (from athletes table)
 * @param relationship - Relationship type (e.g., 'mother', 'father', 'guardian')
 * @param isPrimary - Whether this is the primary parent contact (required — no default)
 * @param hasBillingAccess - Whether parent has billing access (default: true)
 * @param hasMedicalAccess - Whether parent has medical access (default: true)
 * @returns Promise resolving to relationship ID
 *
 * @example
 * // In a component or API route
 * const { data: { user } } = await supabase.auth.getUser();
 * const relationshipId = await createParentAthleteRelationshipByAuthUserId(
 *   user.id,
 *   athlete.id,
 *   'parent',
 *   !(await athleteHasPrimaryParent(athlete.id))
 * );
 */
export async function createParentAthleteRelationshipByAuthUserId(
  authUserId: string,
  athleteId: string,
  relationship: string,
  isPrimary: boolean,
  hasBillingAccess: boolean = true,
  hasMedicalAccess: boolean = true,
  client?: SupabaseClient<Database>
): Promise<string> {
  // SECURITY (#973): the underlying create_parent_athlete_relationship RPC is locked to service_role.
  // Server callers MUST pass their service-role client; the module-default `supabase` is the anon-key
  // browser client (role anon/authenticated) and can no longer execute this RPC.
  const dbClient: SupabaseClient<Database> = client || supabase;
  try {
    logger.info('Creating parent-athlete relationship by auth user ID:', {
      authUserId,
      athleteId,
      relationship,
      isPrimary,
      hasBillingAccess,
      hasMedicalAccess
    });

    // Guard: Validate authUserId exists in auth_users (catches accidental parents.id usage)
    const { data: authUser } = await dbClient
      .from('auth_users')
      .select('id')
      .eq('id', authUserId)
      .maybeSingle();

    if (!authUser) {
      const errMsg = `Invalid parent ID: "${authUserId}" not found in auth_users. ` +
        `Did you accidentally pass parents.id instead of auth_users.id? ` +
        `parent_athlete_relationships.parent_id FK references auth_users.id.`;
      logger.error(errMsg);
      throw new Error(errMsg);
    }

    // parent_athlete_relationships.parent_id = auth_users.id (NOT parents.id)
    const { data: relationshipId, error } = await (dbClient.rpc as any)('create_parent_athlete_relationship', {
      p_parent_id: authUserId, // Direct use of auth_user_id
      p_athlete_id: athleteId,
      p_relationship: relationship,
      p_is_primary: isPrimary,
      p_has_billing_access: hasBillingAccess,
      p_has_medical_access: hasMedicalAccess
    });

    if (error) {
      logger.error('Error creating parent-athlete relationship:', error);
      throw error;
    }

    logger.info('Parent-athlete relationship created successfully:', { relationshipId });
    return relationshipId as string;
  } catch (error) {
    logger.error('Error in createParentAthleteRelationshipByAuthUserId:', error);
    throw error;
  }
}

/**
 * Create parent-athlete relationship using parent record ID
 * 
 * ✅ USE THIS FUNCTION when you already have the auth_user_id
 * 
 * ✅ UPDATED 2025-11-13: parentId now refers to auth_user_id (not parents table ID)
 * This is now functionally identical to createParentAthleteRelationshipByAuthUserId()
 * but kept for backward compatibility.
 * 
 * ⚠️ UPDATED 2026-08-01 (#2781 Phase 3): `relationship` and `isPrimary` are now
 * required — see createParentAthleteRelationshipByAuthUserId.
 *
 * @param parentId - The auth user ID (same as auth_users.id)
 * @param athleteId - The athlete's ID (from athletes table)
 * @param relationship - Relationship type (e.g., 'mother', 'father', 'guardian')
 * @param isPrimary - Whether this is the primary parent contact (required — no default)
 * @param hasBillingAccess - Whether parent has billing access (default: true)
 * @param hasMedicalAccess - Whether parent has medical access (default: true)
 * @returns Promise resolving to relationship ID
 *
 * @example
 * // When you already have auth_user_id
 * const relationshipId = await createParentAthleteRelationshipByParentId(
 *   authUserId,
 *   athlete.id,
 *   'parent',
 *   !(await athleteHasPrimaryParent(athlete.id))
 * );
 */
export async function createParentAthleteRelationshipByParentId(
  parentId: string,
  athleteId: string,
  relationship: string,
  isPrimary: boolean,
  hasBillingAccess: boolean = true,
  hasMedicalAccess: boolean = true,
  client?: SupabaseClient<Database>
): Promise<string> {
  // SECURITY (#973): see note on createParentAthleteRelationshipByAuthUserId — the RPC is service_role-only.
  const dbClient: SupabaseClient<Database> = client || supabase;
  try {
    logger.info('Creating parent-athlete relationship by parent ID:', {
      parentId,
      athleteId,
      relationship,
      isPrimary,
      hasBillingAccess,
      hasMedicalAccess
    });

    // Guard: Validate parentId exists in auth_users (catches accidental parents.id usage)
    const { data: authUser } = await dbClient
      .from('auth_users')
      .select('id')
      .eq('id', parentId)
      .maybeSingle();

    if (!authUser) {
      const errMsg = `Invalid parent ID: "${parentId}" not found in auth_users. ` +
        `Did you accidentally pass parents.id instead of auth_users.id? ` +
        `parent_athlete_relationships.parent_id FK references auth_users.id.`;
      logger.error(errMsg);
      throw new Error(errMsg);
    }

    // parent_athlete_relationships.parent_id = auth_users.id (NOT parents.id)
    const { data: relationshipId, error } = await (dbClient.rpc as any)('create_parent_athlete_relationship', {
      p_parent_id: parentId,
      p_athlete_id: athleteId,
      p_relationship: relationship,
      p_is_primary: isPrimary,
      p_has_billing_access: hasBillingAccess,
      p_has_medical_access: hasMedicalAccess
    });

    if (error) {
      logger.error('Error creating parent-athlete relationship:', error);
      throw error;
    }

    logger.info('Parent-athlete relationship created successfully:', { relationshipId });
    return relationshipId as string;
  } catch (error) {
    logger.error('Error in createParentAthleteRelationshipByParentId:', error);
    throw error;
  }
}

/**
 * Household snapshot returned by getParentAthleteIdsByPhone.
 *
 * - parentName: "Jane Smith" when a parent row owns the phone; NULL when the
 *   phone matches only an 18+ self-managing athlete.
 * - athleteIds: all athletes this phone can check in for within the given
 *   organization. Deduped across parent→athlete and athlete.phone paths so a
 *   shared household line doesn't show the same kid twice.
 */
export interface KioskCheckInHousehold {
  parentName: string | null;
  athleteIds: string[];
}

/**
 * Look up the set of athletes a phone number can check in for at a kiosk,
 * scoped to a single organization.
 *
 * Why org-scoped: the lookup runs on a paired kiosk that belongs to one gym.
 * Without the filter, a parent whose family attends two gyms in CheerVue would
 * see the wrong gym's enrollments, AND an attacker with a paired tablet at
 * Gym A could enumerate families at Gym B using any US phone number.
 *
 * Why fuzzy digit match: parents.phone and athletes.phone store free-form
 * user input — "555-123-4567", "(555) 123-4567", "+1 555 123 4567". We
 * compare on the stripped digits (last 10) so formatting differences don't
 * miss a legitimate match. The caller is expected to have already validated
 * the E.164 format via normalizePhoneForAuth.
 *
 * Adult athletes (athletes.phone = input, status='active') are included even
 * when no parent row matches — this handles 18+ self-managing athletes per
 * CheerVue's existing athlete-self-manage model.
 */
export async function getParentAthleteIdsByPhone(
  client: SupabaseClient<Database>,
  params: {
    /** E.164 format — '+15551234567'. Helper extracts the last-10 digits for match. */
    normalizedPhone: string;
    organizationId: string;
  }
): Promise<KioskCheckInHousehold> {
  const digits = params.normalizedPhone.replace(/\D/g, '');
  const last10 = digits.slice(-10);
  if (last10.length !== 10) {
    return { parentName: null, athleteIds: [] };
  }

  try {
    let parentName: string | null = null;
    let athleteIds: string[] = [];

    // The database narrows to phones containing these ten digits in order
    // (any separators between them); the exact last-10 check below decides.
    // The pattern is built only from digits, so it cannot widen the match.
    // Paged because PostgREST silently stops at 1,000 rows (#5288).
    const phonePattern = `%${last10.split('').join('%')}%`;
    const matchesPhone = (phone: string | null) =>
      (phone || '').replace(/\D/g, '').slice(-10) === last10;

    const { rows: parents, error: parentsError } = await fetchAllPages<{
      auth_user_id: string;
      phone: string | null;
    }>((from, to) =>
      client
        .from('parents')
        .select('auth_user_id, phone')
        .like('phone', phonePattern)
        .order('auth_user_id', { ascending: true })
        .range(from, to)
    );
    if (parentsError) throw parentsError;
    const matchingParentAuthIds = parents
      .filter((p) => matchesPhone(p.phone))
      .map((p) => p.auth_user_id);

    const athleteIdsByParent = new Map<string, string[]>();
    if (matchingParentAuthIds.length > 0) {
      const { data: rels } = await fromParentLinks(client)
        .select('athlete_id, parent_id')
        .in('parent_id', matchingParentAuthIds);
      for (const r of (rels || []) as Array<{ athlete_id: string; parent_id: string }>) {
        athleteIds.push(r.athlete_id);
        athleteIdsByParent.set(r.parent_id, [...(athleteIdsByParent.get(r.parent_id) || []), r.athlete_id]);
      }
    }

    // 18+ self-managing athletes: direct phone on athletes table, active only.
    const { rows: selfAthletes, error: selfAthletesError } = await fetchAllPages<{
      id: string;
      phone: string | null;
    }>((from, to) =>
      client
        .from('athletes')
        .select('id, phone')
        .like('phone', phonePattern)
        .eq('status', 'active')
        .order('id', { ascending: true })
        .range(from, to)
    );
    if (selfAthletesError) throw selfAthletesError;
    const selfAthleteIds = selfAthletes.filter((a) => matchesPhone(a.phone)).map((a) => a.id);

    for (const id of selfAthleteIds) {
      if (!athleteIds.includes(id)) athleteIds.push(id);
    }

    // Org-scope filter: keep only athletes with at least one ACTIVE class
    // enrollment OR CONFIRMED/COMPLETED private lesson booking in this org.
    // Status filters are critical — a stale cancelled enrollment at Gym A
    // should NOT let a Gym B family's phone leak here.
    if (athleteIds.length > 0) {
      const [classScope, lessonScope] = await Promise.all([
        client
          .from('class_enrollments')
          .select('athlete_id')
          .eq('organization_id', params.organizationId)
          .eq('status', 'active')
          .in('athlete_id', athleteIds),
        client
          .from('private_lesson_bookings')
          .select('athlete_id')
          .eq('organization_id', params.organizationId)
          .in('status', ['confirmed', 'completed'])
          .in('athlete_id', athleteIds),
      ]);
      const scopedIds = new Set<string>();
      for (const row of classScope.data || []) scopedIds.add((row as any).athlete_id);
      for (const row of lessonScope.data || []) scopedIds.add((row as any).athlete_id);
      athleteIds = athleteIds.filter((id) => scopedIds.has(id));
    }

    // The name comes only from a parent whose own athlete is at this gym.
    // Otherwise a Gym A tablet could show the name of a Gym B parent who
    // shares the phone number, or confirm the phone belongs to someone.
    const scopedAthleteIds = new Set(athleteIds);
    const namedParentIds = matchingParentAuthIds.filter((id) =>
      (athleteIdsByParent.get(id) || []).some((athleteId) => scopedAthleteIds.has(athleteId))
    );
    if (namedParentIds.length > 0) {
      const { data: authUsers } = await client
        .from('auth_users')
        .select('id, first_name, last_name')
        .in('id', namedParentIds)
        .order('id', { ascending: true })
        .limit(1);
      const primary = authUsers?.[0];
      if (primary) {
        parentName = [primary.first_name, primary.last_name].filter(Boolean).join(' ') || null;
      }
    }

    return { parentName, athleteIds: Array.from(new Set(athleteIds)) };
  } catch (error) {
    logger.error('getParentAthleteIdsByPhone failed', { error });
    return { parentName: null, athleteIds: [] };
  }
}

/**
 * Get parents for an athlete (reverse relationship)
 * 
 * ✅ USE THIS FUNCTION when you need to find which parents are associated with an athlete
 * 
 * @param athleteId - The athlete ID
 * @param includeParentDetails - Whether to include full parent user details (default: true)
 * @param client - Optional Supabase client to use
 * @returns Array of parent-athlete relationships with parent details
 * 
 * @example
 * // Get parents for an athlete
 * const relationships = await getParentsForAthlete(athleteId);
 * const primaryParent = relationships.find(r => r.is_primary);
 */
export async function getParentsForAthlete(
  athleteId: string,
  includeParentDetails: boolean = true,
  client?: SupabaseClient<Database>
): Promise<Array<ParentAthleteRelationship & {
  parent?: {
    id: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
  };
}>> {
  try {
    logger.info('Fetching parents for athlete:', { athleteId });

    const dbClient: SupabaseClient<Database> = client || supabase;

    const selectQuery = includeParentDetails 
      ? `
          *,
          auth_users!parent_athlete_relationships_parent_id_fkey (
            id,
            email,
            first_name,
            last_name
          )
        `
      : '*';

    const { data: relationships, error: relationshipsError } = await dbClient
      .from('parent_athlete_relationships')
      .select(selectQuery)
      .eq('athlete_id', athleteId) as { data: any[] | null; error: any };

    if (relationshipsError) {
      logger.error('Error fetching parents for athlete:', relationshipsError);
      return [];
    }

    logger.info(`Found ${relationships?.length || 0} parents for athlete`, {
      athleteId
    });

    const activeRelationships = await filterRevokedFamilyGuardiansForAthlete(
      dbClient,
      athleteId,
      relationships || []
    );

    // Transform and enrich with parent details
    // Note: Phone numbers are stored in parents table, need to fetch separately
    const enrichedRelationships = await Promise.all(
      activeRelationships.map(async (rel) => {
        const parentData = rel.auth_users;
        
        // Fetch phone number from parents table
        let parentPhone: string | undefined = undefined;
        if (parentData?.id) {
          try {
            const { data: parentRecord } = await dbClient
              .from('parents')
              .select('phone')
              .eq('auth_user_id', parentData.id)
              .maybeSingle();
            
            parentPhone = parentRecord?.phone || undefined;
          } catch (error) {
            logger.warn('Error fetching parent phone:', error);
            // Continue without phone if query fails
          }
        }
        
        return {
          ...rel,
          parent: parentData ? {
            id: parentData.id,
            email: parentData.email,
            first_name: parentData.first_name,
            last_name: parentData.last_name,
            phone: parentPhone
          } : undefined
        };
      })
    );

    return enrichedRelationships;
  } catch (error) {
    logger.error('Error in getParentsForAthlete:', error);
    return [];
  }
}

/**
 * Support ticket #93: Family Access revoke keeps the historical
 * parent_athlete_relationships row, but active parent/guardian displays should
 * show only adults who are still active guardians of the athlete's family. If the
 * family-status reads fail, fail open to the legacy list so we do not hide a
 * real parent because of a read/RLS problem.
 */
async function filterRevokedFamilyGuardiansForAthlete(
  dbClient: SupabaseClient<Database>,
  athleteId: string,
  relationships: any[]
): Promise<any[]> {
  if (!FEATURE_FLAGS.ENABLE_FAMILY_GUARDIAN_ACCESS || relationships.length === 0) {
    return relationships;
  }

  const parentIds = getRelationshipParentIds(relationships);
  if (parentIds.length === 0) {
    return relationships;
  }

  try {
    const familyAccountIds = await readActiveFamilyAccountIdsForAthlete(dbClient, athleteId);
    if (familyAccountIds === null || familyAccountIds.length === 0) {
      return relationships;
    }

    const statusesByGuardian = await readFamilyGuardianStatuses(
      dbClient,
      athleteId,
      familyAccountIds,
      parentIds
    );
    if (statusesByGuardian === null) {
      return relationships;
    }

    return relationships.filter((rel) => shouldDisplayFamilyGuardianRelationship(rel, statusesByGuardian));
  } catch (error) {
    logger.warn('Could not filter revoked family guardians: unexpected error', {
      athleteId,
      error,
    });
    return relationships;
  }
}

function getRelationshipParentIds(relationships: Array<{ parent_id?: unknown }>): string[] {
  return Array.from(
    new Set(
      relationships
        .map((rel) => rel.parent_id)
        .filter((parentId): parentId is string => typeof parentId === 'string' && parentId.length > 0)
    )
  );
}

async function readActiveFamilyAccountIdsForAthlete(
  dbClient: SupabaseClient<Database>,
  athleteId: string
): Promise<string[] | null> {
  const { data: familyAthletes, error } = await dbClient
    .from('family_athletes')
    .select('family_account_id')
    .eq('athlete_id', athleteId)
    .eq('status', 'active') as {
      data: Array<{ family_account_id: string | null }> | null;
      error: any;
    };

  if (error) {
    logger.warn('Could not filter revoked family guardians: family athlete read failed', {
      athleteId,
      error,
    });
    return null;
  }

  return Array.from(
    new Set((familyAthletes || []).map((row) => row.family_account_id).filter(Boolean) as string[])
  );
}

async function readFamilyGuardianStatuses(
  dbClient: SupabaseClient<Database>,
  athleteId: string,
  familyAccountIds: string[],
  parentIds: string[]
): Promise<Map<string, Set<string>> | null> {
  const { data: guardians, error } = await dbClient
    .from('family_guardians')
    .select('family_account_id, guardian_auth_user_id, status')
    .in('family_account_id', familyAccountIds)
    .in('guardian_auth_user_id', parentIds) as {
      data: Array<{ guardian_auth_user_id: string | null; status: string | null }> | null;
      error: any;
    };

  if (error) {
    logger.warn('Could not filter revoked family guardians: guardian read failed', {
      athleteId,
      error,
    });
    return null;
  }

  return buildStatusesByGuardian(guardians || []);
}

function buildStatusesByGuardian(
  guardians: Array<{ guardian_auth_user_id: string | null; status: string | null }>
): Map<string, Set<string>> {
  const statusesByGuardian = new Map<string, Set<string>>();
  for (const guardian of guardians) {
    if (!guardian.guardian_auth_user_id || !guardian.status) continue;
    const statuses = statusesByGuardian.get(guardian.guardian_auth_user_id) ?? new Set<string>();
    statuses.add(guardian.status);
    statusesByGuardian.set(guardian.guardian_auth_user_id, statuses);
  }
  return statusesByGuardian;
}

function shouldDisplayFamilyGuardianRelationship(
  relationship: { parent_id?: unknown },
  statusesByGuardian: Map<string, Set<string>>
): boolean {
  if (typeof relationship.parent_id !== 'string') {
    return true;
  }

  const statuses = statusesByGuardian.get(relationship.parent_id);
  return !statuses || statuses.has('active');
}

/**
 * Organization-scoped relationship access check (U2 Family / Guardian Access).
 *
 * REQUIREMENTS SUMMARY:
 * - ADDITIVE compatibility bridge. Legacy consumers that today assume "any
 *   parent_athlete_relationships row = full access" can call this to verify
 *   access within an organization (and, once the feature is enabled, within the
 *   athlete's family-account context) WITHOUT learning about family internals.
 * - FLAG-OFF behavior (ENABLE_FAMILY_GUARDIAN_ACCESS off): returns true iff an
 *   active parent-athlete relationship exists for (authUserId, athleteId) AND the
 *   athlete belongs to the given organization. This is behavior-neutral — it only
 *   adds an org-scope guard that callers were already implicitly relying on.
 * - FLAG-ON behavior: if `familyAccountId` + a `permission` are supplied, the
 *   final decision is delegated to FamilyAccountService.assertFamilyGuardianPermission
 *   (the canonical boundary) so this helper never re-implements permission logic.
 *   If no familyAccountId/permission is supplied, it falls back to the same
 *   relationship + org-scope check as the flag-off path.
 *
 * Deny-by-default: any error returns false.
 *
 * @param client     - Supabase client (service-role or session-scoped)
 * @param authUserId - auth.users.id of the caller (parent_id space)
 * @param athleteId  - athlete being accessed
 * @param organizationId - org the access must be scoped to
 * @param options.familyAccountId - optional household to evaluate against (flag-on)
 * @param options.permission - optional FamilyPermission to require (flag-on)
 */
export async function assertRelationshipGrantsAccessInOrg(
  client: SupabaseClient<Database>,
  authUserId: string,
  athleteId: string,
  organizationId: string,
  options?: {
    familyAccountId?: string;
    permission?: import('@/types/family').FamilyPermission;
  }
): Promise<boolean> {
  if (!authUserId || !athleteId || !organizationId) {
    return false;
  }

  try {
    // Lazy imports so this module stays free of a hard dependency on the feature
    // flag / family service for the (overwhelmingly common) legacy callers, and
    // to avoid any service↔utility import cycle.
    const { FEATURE_FLAGS } = await import('@/utils/config/featureFlags');

    // Flag-on + explicit household + permission → delegate to the canonical
    // FamilyAccountService boundary. It already verifies active guardian status,
    // active household, org match, role implication, and the money guardrail.
    if (
      FEATURE_FLAGS.ENABLE_FAMILY_GUARDIAN_ACCESS &&
      options?.familyAccountId &&
      options?.permission
    ) {
      const { FamilyAccountService } = await import(
        '@/services/family/FamilyAccountService'
      );
      return await FamilyAccountService.assertFamilyGuardianPermission(client, {
        authUserId,
        familyAccountId: options.familyAccountId,
        permission: options.permission,
      });
    }

    // Baseline (flag-off, or flag-on without an explicit household choice):
    // active relationship row for this parent+athlete...
    const { data: rel, error: relError } = await fromParentLinks(client)
      .select('id')
      .eq('parent_id', authUserId)
      .eq('athlete_id', athleteId)
      .maybeSingle();

    if (relError || !rel) {
      return false;
    }

    // ...and the athlete must belong to this organization.
    const { data: athleteOrg, error: orgError } = await client
      .from('athlete_organizations')
      .select('id')
      .eq('athlete_id', athleteId)
      .eq('organization_id', organizationId)
      .maybeSingle();

    if (orgError || !athleteOrg) {
      return false;
    }

    return true;
  } catch (error) {
    logger.error('assertRelationshipGrantsAccessInOrg failed', {
      authUserId,
      athleteId,
      organizationId,
      error,
    });
    return false;
  }
}
